import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Navigate,
} from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { Provider } from 'react-redux';
import { ConfigProvider, Layout } from 'antd';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  getCurrentUser,
  isUserAuthenticated,
} from './utils/cookieManager';
import { GlobalTheme } from './theme/GlobalTheme';
import { darkTheme, lightTheme } from './theme/themeConfig';
import { store } from './store/store';
import PrivateRoutes from './routes/PrivateRoutes';
import smallLogo from './assets/small-logo.png';
import { ClerkProvider } from '@clerk/clerk-react';
import { getClerkProviderProps } from './utils/portalOrigin';
import { ErrorBoundary, LoaderFallback } from './components/common';
const UsageReporting = lazy(() => import('./pages/UsageReporting'));
const AccountCenter = lazy(() => import('./pages/AccountCenter'));
const Notifications = lazy(() => import('./pages/Notifications'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const SocialAuthHandler = lazy(() => import('./pages/SocialAuthHandler'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AccountVerification = lazy(() => import('./pages/AccountVerification'));
const EmailVerificationRequired = lazy(() => import('./pages/EmailVerificationRequired'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Home = lazy(() => import('./pages/Home'));
const OrganizationRegister = lazy(() => import('./pages/OrganizationRegister'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const Settings = lazy(() => import('./pages/Settings'));
const SuccessPayment = lazy(() => import('./pages/SuccessPayment'));
const CancelPayment = lazy(() => import('./pages/CancelPayment'));
const AccountSettings = lazy(() => import('./components/Settings/AccountSettings'));
const AppPreference = lazy(() => import('./components/Settings/AppPreference'));
const FAQ = lazy(() => import('./components/Settings/FAQ'));
const Feedback = lazy(() => import('./components/Settings/Feedback'));
const NotificationSettings = lazy(() => import('./components/Settings/NotificationSettings'));
const PasswordManagement = lazy(() => import('./components/Settings/PasswordManagement'));
const PrivacyPolicies = lazy(() => import('./components/Settings/PrivacyPolicies'));
const TermsConditions = lazy(() => import('./components/Settings/TermsConditions'));
const App = () => {
  const [isDark, toggleIsDark] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUserData = () => {
    const currentUser = getCurrentUser();
    setUserData(currentUser);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isUserAuthenticated()) {
      refreshUserData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const currentTheme = useMemo(
    () => (isDark ? darkTheme : lightTheme),
    [isDark]
  );
  const logoUrl = smallLogo;
  const PUBLISHABLE_KEY =
    import.meta.env.VITE_CLERK_LIVE_PUBLISHABLE_KEY ||
    import.meta.env.VITE_CLERK_DEV_PUBLISHABLE_KEY;
  const clerkProviderProps = getClerkProviderProps();
  return (
    <Provider store={store}>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        {...clerkProviderProps}
        appearance={{
          layout: {
            logoImageUrl: logoUrl,
          },
          elements: {
            headerTitle: 'SurpassIn',
            headerSubtitle: 'Your Personal Finance Assistant',
            logoImageUrl: logoUrl,
          },
        }}
        onError={(error) => {
          console.error('Clerk error:', error);
        }}
      >
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
          <GlobalTheme
            isDark={isDark}
            toggleIsDark={toggleIsDark}
            theme={currentTheme}
          >
            <ConfigProvider theme={currentTheme}>
              <Layout>
                <Layout className="site-wrapper">
                  <Router>
                    <ErrorBoundary>
                      {isLoading ? (
                        <div>Loading...</div>
                      ) : !userData ? (
                        <Suspense fallback={<LoaderFallback />}>
                          <Routes>
                            <Route path="/auth/login" element={<Login />} />
                            <Route path="/auth/register" element={<Register />} />
                            <Route path="/social-auth/callback" element={<SocialAuthHandler />} />

                            <Route
                              path="/auth/account-verification"
                              element={<AccountVerification />}
                            />
                            <Route
                              path="/auth/email-verification-required"
                              element={<EmailVerificationRequired />}
                            />
                            <Route
                              path="/auth/reset-password"
                              element={<ResetPassword />}
                            />
                            <Route
                              path="/auth/forgot-password"
                              element={<ForgotPassword />}
                            />
                            <Route
                              path="/privacy-policy"
                              element={<PrivacyPolicies />}
                            />
                            <Route
                              path="/terms-&-conditions"
                              element={<TermsConditions />}
                            />
                            <Route
                              path="*"
                              element={<Navigate to="/auth/login" replace />}
                            />
                          </Routes>
                        </Suspense>
                      ) : (
                        <Suspense fallback={<LoaderFallback />}>
                          <Routes>
                            <Route
                              path="/social-auth/callback"
                              element={<SocialAuthHandler />}
                            />
                            <Route element={<PrivateRoutes />}>
                              <Route path="/" element={<Home />}>
                                <Route path="/" element={<UsageReporting />} />
                                <Route
                                  path="admin-settings"
                                  element={<AdminSettings />}
                                />
                                <Route
                                  path="edit-profile"
                                  element={<EditProfile />}
                                />
                                <Route
                                  path="account-centre"
                                  element={<AccountCenter />}
                                />
                                <Route
                                  path="/checkout"
                                  element={<CheckoutPage />}
                                />
                              </Route>
                              <Route path="/settings" element={<Settings />} />
                              <Route
                                path="notifications"
                                element={<Notifications />}
                              />
                              <Route
                                path="/settings/account-settings"
                                element={<AccountSettings />}
                              />
                              <Route
                                path="/settings/account-settings/password-management"
                                element={<PasswordManagement />}
                              />
                              <Route
                                path="/settings/notifications-settings"
                                element={<NotificationSettings />}
                              />
                              <Route path="/settings/faq" element={<FAQ />} />
                              <Route
                                path="/settings/feedback"
                                element={<Feedback />}
                              />
                              <Route
                                path="/settings/app-preference"
                                element={<AppPreference />}
                              />
                              <Route
                                path="/settings/privacy-policy"
                                element={<PrivacyPolicies />}
                              />
                              <Route
                                path="/settings/terms-&-conditions"
                                element={<TermsConditions />}
                              />
                              <Route
                                path="*"
                                element={<Navigate to="/" replace />}
                              />
                              <Route
                                path="/gather-requirements"
                                element={<OrganizationRegister />}
                              />
                            </Route>
                            <Route
                              path="/payment-success"
                              element={<SuccessPayment />}
                            />
                            <Route
                              path="/cancel-success"
                              element={<CancelPayment />}
                            />
                          </Routes>
                        </Suspense>
                      )}
                    </ErrorBoundary>
                  </Router>
                </Layout>
              </Layout>
            </ConfigProvider>
          </GlobalTheme>
        </GoogleOAuthProvider>
      </ClerkProvider>
    </Provider>
  );
};

export default App;
